import React, { useEffect, useState } from 'react'
import { Dropdown } from 'semantic-ui-react'
import { useTranslation } from 'react-i18next'
import { WidgetCard } from '../../style'
import { DashboardQuery } from '~/QueryHooks/DashboardQuery'
import { CurrentMilkPriceWidgetSkeleton } from '../../Skeletons'
import { Price } from '~/api/services/dashboard'
import {
  TableWrapper,
  PriceTable,
  FixedTh,
  EstimateTh,
  LeftHeaderTh,
  GroupCell,
  LabelCell
} from './style'
import { getCorrectLanguageCode } from '~/utils/helpers'
import i18n from '~/components/i18n'

const formatMonthLabel = (isoMonth: string) => {
  const languageCode = getCorrectLanguageCode(i18n.language)
  const date = new Date(isoMonth + '-01')
  const month = date.toLocaleDateString(languageCode, { month: 'short' })
  const year = date.getFullYear()
  return { month, year }
}

const CurrentMilkPriceWidget: React.FC = () => {
  const { t } = useTranslation()
  const [selectedFarmer, setSelectedFarmer] = useState('')

  const priceFields: { key: keyof Price; label: string }[] = [
    { key: 'basePrice', label: t('Base price') },
    { key: 'planningBonus', label: t('Planning bonus') },
    { key: 'ulDeduction', label: t('UL deduction') },
    { key: 'selectionBonus', label: t('mooh sustainable (A-Segment)') }
  ]

  const {
    data: milkMoneyOptions,
    refetch: fetchOptions
  } = DashboardQuery.getDashboardMilkMoneyOptions()

  const effectiveFarmer = selectedFarmer || milkMoneyOptions?.farmers?.[0]?.farmerId || ''

  const {
    data: segmentationData,
    isLoading,
    isFetching,
    refetch: fetchData
  } = DashboardQuery.getDashboardSegmentationData(effectiveFarmer || undefined)

  useEffect(() => {
    fetchData()
    fetchOptions()
  }, [t])

  const farmerOptions = (milkMoneyOptions?.farmers ?? []).map((farmer) => ({
    key: farmer.farmerId,
    value: farmer.farmerId,
    text: farmer.companyName
  }))

  if (isLoading || isFetching) return <CurrentMilkPriceWidgetSkeleton />

  const tradingMonths = segmentationData?.prices?.filter(p => p.priceSet === 1) ?? []
  const futureTradingMonths = segmentationData?.prices?.filter(p => p.priceSet === 0) ?? []
  const allPrices = [...tradingMonths, ...futureTradingMonths]

  const milkTypeMap = Object.values(
    allPrices.reduce<Record<number, { id: number; name: string }>>((acc, price) => {
      if (!acc[price.milkTypeId]) {
        acc[price.milkTypeId] = { id: price.milkTypeId, name: price.milkType.name }
      }
      return acc
    }, {})
  )

  const toMonthKey = (p: Price) => new Date(p.month).toISOString().slice(0, 7)

  const tradingMonthKeys = [...new Set(
    [...tradingMonths].sort((a, b) => toMonthKey(a).localeCompare(toMonthKey(b))).map(toMonthKey)
  )]
  const futureMonthKeys = [...new Set(
    [...futureTradingMonths].sort((a, b) => toMonthKey(a).localeCompare(toMonthKey(b))).map(toMonthKey)
  )]

  const buildLookup = (prices: Price[]) =>
    prices.reduce<Record<number, Record<string, Price>>>((acc, price) => {
      const key = toMonthKey(price)
      if (!acc[price.milkTypeId]) acc[price.milkTypeId] = {}
      acc[price.milkTypeId][key] = price
      return acc
    }, {})

  function renderNoResult() {
    return (
      <WidgetCard>
        <p>{t('No data available')}</p>
      </WidgetCard>
    )
  }

  function renderTable() {
    return <TableWrapper>
      <PriceTable>
        <thead>
          <tr>
            <LeftHeaderTh colSpan={2} rowSpan={2} style={{ verticalAlign: 'middle' }}>
              {t('Seasonal prices (Rp./kg)')}<br />
              <span style={{ fontWeight: 400, fontSize: '11px' }}>
                {t('(without traffic milk allowance)')}
              </span>
            </LeftHeaderTh>
            {tradingMonthKeys.length > 0 && (
            <FixedTh colSpan={tradingMonthKeys.length}>
              {t('Fixed prices')}
            </FixedTh>
            )}
            {futureMonthKeys.length > 0 && (
            <EstimateTh colSpan={futureMonthKeys.length}>
              <span>{t('Estimation')}</span>
            </EstimateTh>
            )}
          </tr>
          <tr>
            {tradingMonthKeys.map(key => {
              const { month, year } = formatMonthLabel(key)
              return (
                <FixedTh key={key}>
                  {month}<br />{year}
                </FixedTh>
              )
            })}
            {futureMonthKeys.map(key => {
              const { month, year } = formatMonthLabel(key)
              return (
                <EstimateTh key={key}>
                  {month}<br />{year}
                </EstimateTh>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {milkTypeMap.map(({ id, name }) =>
            priceFields.map((field, fieldIdx) => (
              <tr key={`${id}-${field.key}`}>
                {fieldIdx === 0 && (
                <GroupCell rowSpan={priceFields.length} colSpan={1}>
                  {name}
                </GroupCell>
                )}
                <LabelCell>{field.label}</LabelCell>
                {tradingMonthKeys.map(monthKey => {
                  const price = tradingLookup[id]?.[monthKey]
                  return <td key={monthKey}>{price != null ? price[field.key] as string ?? '-' : '-'}</td>
                })}
                {futureMonthKeys.map(monthKey => {
                  const price = futureLookup[id]?.[monthKey]
                  return <td key={monthKey}>{price != null ? price[field.key] as string ?? '-' : '-'}</td>
                })}
              </tr>
            ))
          )}
        </tbody>
      </PriceTable>
    </TableWrapper>
  }

  const tradingLookup = buildLookup(tradingMonths)
  const futureLookup = buildLookup(futureTradingMonths)

  return (
    <WidgetCard>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
        <Dropdown
          selection
          options={farmerOptions}
          value={effectiveFarmer}
          onChange={(_, { value }) => setSelectedFarmer(value as string)}
          placeholder={t('Choose farm')}
        />
      </div>
      {allPrices?.length === 0 ? renderNoResult() : renderTable()}
    </WidgetCard>
  )
}

export default CurrentMilkPriceWidget
